<template>
  <!--签章前的预览-->
  <el-dialog
    v-if="step === 1"
    :visible.sync="visible"
    top="7vh"
    width="1200px"
    class="dialog"
    :title="title"
    :close-on-click-modal="false"
    append-to-body
    @close="() => clearDialog(false)"
  >
    <div class="content-wrapper flex flex-col">
      <template v-if="isBusinessSideSign && !taskId">
        <div class="text-danger">
          所有文件在签章时，都会转成PDF格式，请认真预览下方文件
        </div>
        <div class="flex font-14 m-t-8">
          excel文件，如果需求确保在签章时内容最大化展示，请先将纸张方向调整为
          <span class="text-danger">横向</span>
          <excel-tip class="m-l-10">
            <span class="text-primary font-14">操作步骤</span>
          </excel-tip>
        </div>

        <div
          v-if="isSigner"
          class="flex font-12 text-info row-center m-t-8 text-danger"
        >
          <span class="signature-tip">
            每签章一份文件， 次数减少一次
          </span>
          <span class="signature-count m-l-24">
            剩余签章次数：
            <span class="text-color">{{ signatureCount }}</span>
          </span>
        </div>
      </template>

      <!--非业务端发起的弹窗-->
      <template v-else>
        <div class="file-title">
          {{ fileName }}
        </div>
        <div class="flex font-12 text-info row-center m-t-8 text-danger">
          <span class="signature-tip">
            每签章一份文件， 次数减少一次
          </span>
          <span class="signature-count m-l-24">
            剩余签章次数：
            <span class="text-color">{{ signatureCount }}</span>
          </span>
        </div>
      </template>

      <!--pdf预览区域-->
      <iframe
        :src="`/pdfjs/web/viewer.html?file=${encodeURIComponent(fileUrl)}`"
        frameborder="0"
        class="frame m-t-24 flex-1"
      ></iframe>

      <div class="flex row-center m-t-16">
        <el-button
          v-if="isBusinessSideSign && !taskId && !isSigner"
          type="primary"
          style="width: 120px"
          @click="sendSignatureTask"
        >
          确定发送
        </el-button>
        <el-button v-else type="primary" style="width: 120px" @click="confirm">
          签 章
        </el-button>
      </div>
    </div>
  </el-dialog>

  <!--签章弹窗-->
  <el-drawer
    v-else
    title="在线签章（请不要使用浏览器的无痕模式）"
    :visible.sync="visible"
    size="90%"
    @close="clearDialog"
  >
    <iframe
      key="signaturePreview"
      :src="signatureUrl"
      frameborder="0"
      class="frame"
      :loading="iframeLoading"
      onload="iframeLoading = false"
    ></iframe>
  </el-drawer>
</template>

<script>
// 组件
import ExcelTip from '@/components/common/signature/ExcelTip'

// api接口
import {
  getSignatureRemainingCountApi,
  signESignTaskApi,
  getESignFileUrlApi,
  addESignTaskApi
} from '@/api/signature'

// 工具函数
import { mapState } from 'vuex'

// 数据字典
import { signatureStatusDict } from '@/dictionary/signature'

export default {
  name: 'SignaturePreviewDialog',

  components: {
    ExcelTip
  },

  /**
   * 自定义的方法：
   * openSignatureWindow: 目前用beforeSignatureFun代替
   * closeSignatureWindow
   */
  props: {
    // sync
    dialogVisible: {
      type: Boolean,
      default: false
    },

    // 预览将被签章的文件地址
    fileUrl: {
      type: String,
      default: ''
    },

    // 预览的附件名称
    fileName: {
      type: String,
      default: ''
    },

    // 是否为业务端的自签行为，默认为true
    isBusinessSideSign: {
      type: Boolean,
      default: true
    },

    // 客户端自签时需要，需要签署的附件id
    attachmentId: {
      type: [String, Number],
      default: ''
    },

    // 客户端自签需要使用，若传入的非pdf，则这样需要的是转换后的pdf文件id
    previewAttachmentId: {
      type: [String, Number],
      default: ''
    },

    // 客户端自签使用，签章前先保存数据
    beforeSignatureFun: {
      type: Function,
      default: () => Promise.resolve()
    },

    taskId: {
      type: [String, Number],
      default: ''
    },

    taskStatus: {
      type: Number,
      default: 0
    },

    // 任务描述 -- 用于在签章任务列表中查看到关于任务的一些信息，（一般情况下传项目名称）
    taskDesc: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      signatureCount: 0,
      step: 1, // 步骤1：签章预览，步骤2：签章
      signatureUrl: '', // 签章地址
      iframeLoading: false
    }
  },

  computed: {
    ...mapState('user', ['isSigner']),

    title() {
      if (this.isBusinessSideSign && !this.taskId) {
        return `签章时文件样式模拟 （${this.fileName}）`
      } else {
        return '在线签章'
      }
    },

    visible: {
      get() {
        return this.dialogVisible
      },
      set(val) {
        this.$emit('update:dialogVisible', val)
      }
    }
  },

  watch: {
    dialogVisible(val) {
      if (!val) return

      this.step = 1
      this.getSignatureRemainingCount()
    }
  },

  methods: {
    /**
     * 获取可签章次数
     */
    getSignatureRemainingCount() {
      return getSignatureRemainingCountApi()
        .then(res => {
          this.signatureCount = res.data || 0
          return Promise.resolve(res.data || 0)
        })
        .catch(err => {
          this.$message.error(err?.message || '获取签章剩余次数失败')
        })
    },

    clearDialog(needRefresh = true) {
      this.signatureCount = 0

      if (needRefresh) {
        this.$nextTick(() => {
          this.$emit('closeSignatureWindow')
        })
      }
    },

    // 业务端发起签章，
    sendSignatureTask() {
      this.beforeSignatureFun(this.attachmentId)
        .then(() => {
          return addESignTaskApi({
            attachmentId: this.attachmentId,
            taskName: this.taskDesc,
            type: 2
          }).then(res => {
            this.$emit('closeSignatureWindow')
            this.$alert('已发送给签章人员签章，请稍后。', '系统提示', {
              confirmButtonText: '确定',
              callback: () => {
                this.visible = false
              }
            })
          })
        })
        .catch(err => {
          this.$message.error(err?.message || '签章任务发送失败')
        })
    },

    async confirm() {
      let requestApi = () => getESignFileUrlApi(this.taskId)

      // 如果不是业务端发起的，则这里为发起签章，则需要校验签章次数
      if (
        !this.taskId ||
        this.taskStatus === signatureStatusDict.TO_BE_SIGNED.value
      ) {
        try {
          requestApi = this.isBusinessSideSign
            ? () =>
              this.beforeSignatureFun(this.attachmentId).then(() => {
                // 业务端发起签章
                return addESignTaskApi({
                  attachmentId: this.attachmentId,
                  taskName: this.taskDesc,
                  type: 1
                }).then(res => {
                  return getESignFileUrlApi(res.data)
                })
              })
            : () => signESignTaskApi(this.taskId) // 签章任务页面发起的签章

          const count = await this.getSignatureRemainingCount()
          if (!count) {
            this.$systemConfirm(
              '当前企业剩余签章次数为0，请先购买签章次数',
              '系统提示',
              '立即购买'
            ).then(result => {
              if (result) {
                this.$router.push({ name: 'SignatureBuy' })
              }
            })
            return
          }
        } catch (e) {
          return
        }
      }

      // 获取第三方签章网址
      requestApi()
        .then(res => {
          this.step = 2
          this.iframeLoading = true
          this.signatureUrl = res.data
        })
        .catch(err => {
          this.$message.error(err?.message || '签章任务发起失败')
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.file-title {
  font-size: 20px;
  @include text-bold;
  text-align: center;
}

::v-deep .el-drawer__header {
  padding-bottom: 18px !important;
  margin-bottom: 0 !important;
  font-size: 18px;
}
.frame {
  width: 100%;
  height: calc(100% - 4px);
}

.content-wrapper {
  height: calc(86vh - 110px);

  .frame {
    width: 100%;
  }
}
</style>
